import { Controller } from "@hotwired/stimulus"

/**
 * Segmented control for switching a report card between its chart and table
 * views.
 *
 * Attached to the group WRAPPER (not a single button) with:
 *   data-controller="chart-table-toggle"
 *   data-chart-table-toggle-target-value="<canvas_id>"
 *   data-chart-table-toggle-table-id-value="<table_id>"   (optional)
 *
 * The wrapper holds two buttons:
 *   data-chart-table-toggle-target="chartBtn"  data-action="click->chart-table-toggle#showChart"
 *   data-chart-table-toggle-target="tableBtn"  data-action="click->chart-table-toggle#showTable"
 *
 * The active view is highlighted on its button via the `is-active` class and
 * `aria-pressed`. If no explicit table-id-value is provided, we look for a
 * sibling element with id "<canvas_id>-table" (a conventional default). If no
 * table exists — common in Phase 1 where the data-table rendering is deferred
 * — the action is a silent no-op and the highlight stays on the chart view.
 *
 * Bar-only helper text (e.g. "Click a bar to drill in…") still collapses in
 * table view, scoped to the enclosing .report-chart-card so unrelated cards
 * on the page are never touched.
 */
export default class extends Controller {
  static targets = ["chartBtn", "tableBtn"]
  static values = {
    target:  { type: String, default: "" },   // canvas id
    tableId: { type: String, default: "" }    // table id (optional)
  }

  connect() {
    this.showingTable = false
    this._syncButtons()
  }

  showChart(event) { this._setView(false, event) }
  showTable(event) { this._setView(true, event) }

  _setView(showTable, event) {
    if (event) event.preventDefault()
    if (showTable === this.showingTable) return

    const chartNode = document.getElementById(this.targetValue)
    const table = document.getElementById(this._resolvedTableId())
    if (!chartNode || !table) {
      // Silent no-op when the table sibling hasn't been rendered yet —
      // the highlight stays on the chart view.
      return
    }

    this.showingTable = showTable
    chartNode.classList.toggle("d-none", showTable)
    table.classList.toggle("d-none", !showTable)

    this._barOnlyHelpers().forEach((el) => {
      el.classList.toggle("d-none", showTable)
    })

    this._syncButtons()
  }

  _syncButtons() {
    this._paint(this.hasChartBtnTarget ? this.chartBtnTarget : null, !this.showingTable)
    this._paint(this.hasTableBtnTarget ? this.tableBtnTarget : null, this.showingTable)
  }

  _paint(btn, active) {
    if (!btn) return
    btn.classList.toggle("is-active", active)
    btn.setAttribute("aria-pressed", active ? "true" : "false")
  }

  _resolvedTableId() {
    if (this.tableIdValue) return this.tableIdValue
    return this.targetValue ? `${this.targetValue}-table` : ""
  }

  _barOnlyHelpers() {
    const card = this.element.closest(".report-chart-card")
    if (!card) return []
    return Array.from(card.querySelectorAll("[data-chart-table-toggle-bar-only]"))
  }
}
